import * as React from 'react'

import { Box, Button, Href, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  DeletePayrollDocumentDocument,
  DeletePayrollDocumentMutation,
  DocumentFragment,
  PayrollDocumentFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import DocumentType from 'modules/core/components/DocumentType'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import buildUrl from 'modules/core/lib/buildUrl'
import { formatTime } from 'modules/core/lib/formatTime'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  document: DocumentFragment
  payrollDocuments?: PayrollDocumentFragment[]
  reloadFiles?: () => void
  isDeleteAble?: boolean
  documentOption?: string
}

const FileBox = ({
  document,
  payrollDocuments,
  reloadFiles,
  isDeleteAble = true,
  documentOption,
}: Props) => {
  const { access } = useCurrentSession()
  const [isOpen, toggleOpen] = React.useState(false)
  const [deletePayrollDocument, { loading: removingDocument }] = useMutation(
    DeletePayrollDocumentDocument,
    {
      extractorFn: (data: DeletePayrollDocumentMutation) =>
        data.payrollMutations.destroyPayrollPayrollDocument,
    }
  )

  const payrollDocument =
    payrollDocuments && payrollDocuments.find((doc) => doc.document?.id === document.id)
  const uploadBy = () => {
    const authorName = document.author?.fullName

    if (!authorName) return null

    if (documentOption) {
      return ` - ${i18n.t('documents.downloaded_by', {
        authorName,
      })}`
    }

    return ` - ${i18n.t('documents.uploaded_by', {
      authorName,
    })}`
  }

  const bookkeeperDownloadUrl = (file: string | null | undefined): string => {
    const url = new URL(buildUrl(file))
    url.searchParams.set('bookkeeper_view', 'true')

    return url.toString()
  }

  const formattedDatetime = () => {
    if (!document.arCreatedAt) return i18n.t('common.unknown')
    const date = moment.unix(document.arCreatedAt)
    const formattedDate = date.format('ll')
    const formattedTime = formatTime(access, date.format('HH:mm'))

    return `${formattedDate}, ${formattedTime}`
  }

  const showSyncConfirmation = (message: string) => {
    app.setFlash({
      type: 'correct',
      message: message,
    })
  }

  const showErrors = (errors: Record<string, string>) => {
    app.setFlash({
      type: 'wrong',
      message: errors.message || i18n.t('common.unknown'),
    })
  }

  const deleteDocument = async () => {
    if (!payrollDocument) return

    const response = await deletePayrollDocument({
      context: {
        isBookkeeperView: true,
      },
      variables: {
        id: payrollDocument.id,
      },
    })

    const result = handleResult(response, (errors) => {
      showErrors(errors)
    })

    if (result.success) {
      showSyncConfirmation(i18n.t('common.removed_successfully'))
    }

    toggleOpen(false)
    reloadFiles?.()
  }

  return (
    <>
      <Box
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
        width="full"
        borderRadius={{ all: 'abs008' }}
        padding="s16"
        flexDirection="row"
        justifyContent="spaceBetween"
      >
        <Box flexDirection="row" gap="s4" maxWidth="full" overflowX="hidden">
          <DocumentType mimetype={document.contentType || 'text'} />
          <Box flexGrow={false} maxWidth="full" overflowX="hidden">
            <Text.Normal ellipsis>{document.filename}</Text.Normal>
            <Box paddingTop="s4">
              <Text.Normal size="200" color="grey800" ellipsis>
                {formattedDatetime()}
                {uploadBy()}
              </Text.Normal>
            </Box>
          </Box>
        </Box>
        <Box flexDirection="row">
          {document.file && (
            <Href
              href={bookkeeperDownloadUrl(document.file)}
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              <Box flexNoShrink>
                <Icon.Medium icon="download" />
              </Box>
            </Href>
          )}
          {isDeleteAble && (
            <Box flexNoShrink component="button" onClick={() => toggleOpen(true)}>
              <Icon.Medium icon="delete" />
            </Box>
          )}
        </Box>
      </Box>

      {isOpen && (
        <Modal
          illustration="delete"
          onClose={() => toggleOpen(false)}
          title="Are you sure you want to delete this document?"
          description="This action will delete the payroll document permanently."
          footer={
            <>
              <Button type="secondary" onClick={() => toggleOpen(false)}>
                Cancel
              </Button>
              <Button type="primary" disabled={removingDocument} onClick={deleteDocument}>
                Delete
              </Button>
            </>
          }
        />
      )}
    </>
  )
}

export default FileBox
